import type { BuildMode } from '../input/BuildMode.js';
import type { ArmedMode, InputRouter } from '../input/InputRouter.js';

const BANNER_ID = 'armed-banner';
const STYLE_ID = 'armed-banner-styles';

interface ArmedBannerOptions {
  getRouter: () => InputRouter | null;
  buildMode: BuildMode;
}

// V3 mobile: visible feedback for every "armed" state so the player knows
// exactly which tap is expected next. Desktop's silent-arm flow (the cursor
// just changes context) is invisible on a touch device — without this banner
// a player would hit Q on a Recon and have no idea their next tap is now a
// Mark-Target click. The banner sits across the top under the timer and
// always carries a prominent Cancel button.
//
// Reads exclusively through the InputRouter / BuildMode public APIs (Rule 2 —
// presentation only). Polls per-frame via the same scene update tick the HUD
// uses (driven by ClientApp wiring) so we don't need to subscribe to internal
// arming state.
export class ArmedBanner {
  private opts: ArmedBannerOptions;
  private container: HTMLDivElement;
  private label: HTMLElement;
  private cancelBtn: HTMLButtonElement;
  private currentMode: ArmedMode = 'idle';
  private unsubscribeBuild: (() => void) | null = null;

  constructor(opts: ArmedBannerOptions) {
    this.opts = opts;
    injectStyles();
    this.container = ensureDiv(BANNER_ID, 'armed-banner');
    this.label = document.createElement('div');
    this.label.className = 'armed-banner__label';
    this.cancelBtn = document.createElement('button');
    this.cancelBtn.type = 'button';
    this.cancelBtn.className = 'armed-banner__cancel';
    this.cancelBtn.textContent = 'Cancel';
    this.cancelBtn.setAttribute('aria-label', 'Cancel armed action');
    this.cancelBtn.addEventListener('click', () => {
      const router = this.opts.getRouter();
      router?.triggerCancel();
    });
    this.container.appendChild(this.label);
    this.container.appendChild(this.cancelBtn);
    this.unsubscribeBuild = opts.buildMode.subscribe(() => this.refresh());
    this.container.style.display = 'none';
  }

  // Called from the same per-frame tick as HUD update — keeps the banner in
  // sync with armed-state changes that originate from keyboard (desktop with
  // touchscreen) without needing extra subscriptions inside InputRouter.
  refresh(): void {
    const router = this.opts.getRouter();
    const mode: ArmedMode = router === null ? 'idle' : router.getArmedMode();
    if (mode === this.currentMode) return;
    this.currentMode = mode;
    if (mode === 'idle') {
      this.container.style.display = 'none';
      return;
    }
    this.container.style.display = 'flex';
    this.container.dataset.mode = mode;
    this.label.textContent = labelFor(mode);
  }

  hide(): void {
    this.container.style.display = 'none';
    this.currentMode = 'idle';
  }

  dispose(): void {
    this.unsubscribeBuild?.();
    this.unsubscribeBuild = null;
    this.container.remove();
  }
}

function labelFor(mode: ArmedMode): string {
  switch (mode) {
    case 'mark-target':
      return 'Tap an enemy squad to Mark Target';
    case 'cut-link':
      return 'Tap a relay to Cut Link';
    case 'build':
      return 'Tap a build slot on your Core';
    case 'idle':
    default:
      return '';
  }
}

function ensureDiv(id: string, className: string): HTMLDivElement {
  const existing = document.getElementById(id);
  if (existing instanceof HTMLDivElement) {
    existing.className = className;
    return existing;
  }
  const div = document.createElement('div');
  div.id = id;
  div.className = className;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${BANNER_ID} {
  position: fixed;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  display: none;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: rgba(8,12,18,0.94);
  border: 1px solid #f5e76e;
  border-radius: 6px;
  z-index: 22;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #f5e76e;
  font-size: 12px;
  letter-spacing: 0.06em;
  box-shadow: 0 6px 22px rgba(0,0,0,0.55);
  /* Soft pulse animation so the player's eye snaps to it immediately. */
  animation: armed-banner-pulse 1.4s ease-in-out infinite;
  max-width: calc(100vw - 24px);
  white-space: nowrap;
}
#${BANNER_ID}[data-mode="build"] { border-color: #6ed4f5; color: #6ed4f5; }
#${BANNER_ID}[data-mode="cut-link"] { border-color: #e2543c; color: #ffb3a3; }
.armed-banner__label {
  flex: 1;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
}
.armed-banner__cancel {
  font-family: inherit;
  font-size: 11px;
  padding: 6px 12px;
  background: #1f262e;
  color: #eef3f8;
  border: 1px solid #3a444f;
  border-radius: 4px;
  cursor: pointer;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  min-height: 32px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.armed-banner__cancel:active { background: #2c3a4a; transform: scale(0.96); }
@keyframes armed-banner-pulse {
  0%, 100% { box-shadow: 0 6px 22px rgba(0,0,0,0.55), 0 0 0 0 rgba(245,231,110,0.45); }
  50% { box-shadow: 0 6px 22px rgba(0,0,0,0.55), 0 0 0 8px rgba(245,231,110,0); }
}
@media (max-width: 820px), (pointer: coarse) {
  /* V5 mobile UX pass — the standalone BuildPanel is hidden on mobile, so
   * the armed banner can sit at one consistent height across all modes.
   * It anchors just above the action bar in the same screen-strip the
   * context panel occupies, but with enough z-index headroom so they
   * don't stack visually (the banner is 1 line of text, ~32 px tall).
   * Cancel button is enlarged to clear the Apple HIG 44 px tap-target
   * floor — it's the one button that backs out of a wrong armed verb,
   * so it must be reliably hittable. */
  #${BANNER_ID} {
    top: auto;
    bottom: calc(150px + env(safe-area-inset-bottom, 0px));
    padding: 8px 12px;
    font-size: 11px;
    letter-spacing: 0.04em;
    gap: 10px;
    max-width: calc(100vw - 16px);
    white-space: normal;
    text-align: center;
  }
  .armed-banner__cancel {
    font-size: 12px;
    padding: 10px 16px;
    min-height: 44px;
    font-weight: 600;
  }
}
@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  #${BANNER_ID} {
    bottom: calc(118px + env(safe-area-inset-bottom, 0px));
    padding: 6px 10px;
  }
  .armed-banner__cancel {
    min-height: 38px;
    padding: 8px 12px;
    font-size: 11px;
  }
}
@media (prefers-reduced-motion: reduce) {
  #${BANNER_ID} { animation: none; }
}
`;
  document.head.appendChild(style);
}
